/**
 * AI Code Governance - Demo Mode
 *
 * Route: /code-governance/demo | Nav: Code > AI Code Governance > Demo
 *
 * Sales demo page: simulate PR enforcement against seeded sample diffs.
 * Each scenario shows expected decision, findings, and a mock PR comment
 * preview of what LeftShield would post to GitHub.
 */
import React, { useState } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Caption1,
  Card,
  CardHeader,
  Badge,
  Button,
  tokens,
  makeStyles,
  Divider,
} from '@fluentui/react-components';
import {
  PlayRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
} from '@fluentui/react-icons';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';

// ---------------------------------------------------------------------------
// Custom styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  scenarioGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
    gap: tokens.spacingHorizontalXL,
  },
  scenarioCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: tokens.spacingVerticalM,
  },
  resultContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  findingItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusSmall,
  },
  prComment: {
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase300,
    padding: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    whiteSpace: 'pre-wrap' as const,
    overflowX: 'auto' as const,
  },
  commentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
});

// ---------------------------------------------------------------------------
// Demo data
// ---------------------------------------------------------------------------

type Decision = 'BLOCK' | 'PASS';

interface SimFinding {
  policyKey: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM';
  title: string;
  impact: string;
  fix: string;
}

interface DemoScenario {
  id: string;
  name: string;
  description: string;
  expectedDecision: Decision;
  codeDiff: string;
  findings: SimFinding[];
  prCommentPreview: string;
}

const scenarios: DemoScenario[] = [
  {
    id: 'sim1',
    name: 'Unsafe Payment Route',
    description: 'A payment handler reads the amount directly from the request body without any validation or bounds checking. If merged, a crafted request with a negative amount could reverse charges.',
    expectedDecision: 'BLOCK',
    codeDiff: `// PaymentController.cs
+ [HttpPost("charge")]
+ public async Task<IActionResult> Charge([FromBody] ChargeRequest req)
+ {
+     await _stripe.ChargeAsync(req.Amount, req.Currency);
+     return Ok();
+ }`,
    findings: [
      {
        policyKey: 'AI-APP-001',
        severity: 'HIGH',
        title: 'Unvalidated payment amount from request body',
        impact: 'Would allow negative or extreme charge amounts — direct financial loss',
        fix: 'Add validation: Amount must be > 0 and <= configured maximum. Use FluentValidation.',
      },
    ],
    prCommentPreview: `## LeftShield AI Code Governance Gate

**Decision: BLOCK**

### Findings (1)

| Severity | Policy | Finding |
|----------|--------|---------|
| HIGH | AI-APP-001 | Unvalidated payment amount from request body |

### Impact
This PR would allow negative or extreme charge amounts to be processed, causing direct financial loss.

### Recommended Action
Add input validation before processing the payment. Amount must be > 0 and within configured limits.

---
_Proof record generated. Blocking merge until findings are resolved._`,
  },
  {
    id: 'sim2',
    name: 'Leaked API Secret',
    description: 'A developer hardcoded a Stripe live API key directly in the service class. Once pushed to the repository, the key is permanently in version history even if deleted later.',
    expectedDecision: 'BLOCK',
    codeDiff: `// StripeService.cs
+ private const string ApiKey = "sk_live_4eC39HqLyjWDarjtT1zdp7dc";
+
+ public async Task ProcessPayment(decimal amount)
+ {
+     StripeConfiguration.ApiKey = ApiKey;
+     // ...
+ }`,
    findings: [
      {
        policyKey: 'AI-SEC-001',
        severity: 'CRITICAL',
        title: 'Hardcoded Stripe live secret key in source code',
        impact: 'Would permanently expose production payment credentials in repository history — key rotation required immediately',
        fix: 'Move the key to Azure Key Vault. Reference via IConfiguration["Stripe:ApiKey"]. Never commit secrets.',
      },
    ],
    prCommentPreview: `## LeftShield AI Code Governance Gate

**Decision: BLOCK**

### Findings (1)

| Severity | Policy | Finding |
|----------|--------|---------|
| CRITICAL | AI-SEC-001 | Hardcoded Stripe live secret key in source code |

### Impact
This PR contains a production Stripe API key (sk_live_...). Once merged, this key would be permanently visible in repository history. Attackers scanning public/leaked repos routinely harvest such keys.

### Recommended Action
1. Remove the hardcoded key immediately
2. Store in Azure Key Vault or environment variables
3. Rotate the compromised key in the Stripe dashboard

---
_Proof record generated. Blocking merge until findings are resolved._`,
  },
  {
    id: 'sim3',
    name: 'Insecure Admin Bypass',
    description: 'An admin route is added without any authorization middleware. Any authenticated user — including free-tier customers — could access user management, billing, and deletion endpoints.',
    expectedDecision: 'BLOCK',
    codeDiff: `// AdminController.cs
+ [ApiController]
+ [Route("api/admin")]
+ public class AdminController : ControllerBase
+ {
+     [HttpDelete("users/{id}")]
+     public async Task<IActionResult> DeleteUser(Guid id)
+     {
+         await _userService.DeleteAsync(id);
+         return NoContent();
+     }
+ }`,
    findings: [
      {
        policyKey: 'AI-AUTH-001',
        severity: 'CRITICAL',
        title: 'Admin endpoint missing authorization check',
        impact: 'Would allow any authenticated user to delete other users — complete privilege escalation',
        fix: 'Add [Authorize(Roles = "System.Admin")] to the controller or individual actions.',
      },
    ],
    prCommentPreview: `## LeftShield AI Code Governance Gate

**Decision: BLOCK**

### Findings (1)

| Severity | Policy | Finding |
|----------|--------|---------|
| CRITICAL | AI-AUTH-001 | Admin endpoint missing authorization check |

### Impact
This PR exposes a user deletion endpoint without any role check. Any authenticated user could call DELETE /api/admin/users/{id}, deleting arbitrary accounts including other administrators.

### Recommended Action
Add [Authorize(Roles = "System.Admin")] attribute to the AdminController class.

---
_Proof record generated. Blocking merge until findings are resolved._`,
  },
  {
    id: 'sim4',
    name: 'Unsafe Dynamic Execution',
    description: 'A template rendering function passes user-supplied input directly to eval(). An attacker could execute arbitrary JavaScript on the server, gaining full system access.',
    expectedDecision: 'BLOCK',
    codeDiff: `// templateEngine.ts
+ export function renderTemplate(template: string, data: any): string {
+   const fn = eval(\`(\${template})\`);
+   return fn(data);
+ }`,
    findings: [
      {
        policyKey: 'AI-EXEC-001',
        severity: 'CRITICAL',
        title: 'eval() called with user-supplied template string',
        impact: 'Would enable remote code execution (RCE) — attacker gains full server access',
        fix: 'Replace eval() with a sandboxed template engine (Handlebars, Mustache, or Liquid with strict mode).',
      },
    ],
    prCommentPreview: `## LeftShield AI Code Governance Gate

**Decision: BLOCK**

### Findings (1)

| Severity | Policy | Finding |
|----------|--------|---------|
| CRITICAL | AI-EXEC-001 | eval() called with user-supplied template string |

### Impact
This PR uses eval() to execute user-provided template strings. An attacker could submit a template containing \`process.exit(1)\` or \`require('child_process').exec('...')\`, gaining complete control of the server.

### Recommended Action
Replace eval() with a sandboxed template engine. Handlebars with strict mode or Liquid are safe alternatives.

---
_Proof record generated. Blocking merge until findings are resolved._`,
  },
  {
    id: 'sim5',
    name: 'Safe Validated Request',
    description: 'A properly implemented signup handler with input validation, parameterized queries, no hardcoded secrets, and full test coverage. This is what a passing PR looks like.',
    expectedDecision: 'PASS',
    codeDiff: `// SignupController.cs
+ [HttpPost("signup")]
+ public async Task<IActionResult> Signup(
+     [FromBody] SignupRequest req)
+ {
+     var result = await _validator.ValidateAsync(req);
+     if (!result.IsValid)
+         return BadRequest(result.Errors);
+
+     var user = await _userService.CreateAsync(
+         req.Email, req.Name);
+     return Created($"/users/{user.Id}", user);
+ }`,
    findings: [],
    prCommentPreview: `## LeftShield AI Code Governance Gate

**Decision: PASS**

All governance checks passed. No findings detected.

| Check | Result |
|-------|--------|
| Input Validation | Validated via FluentValidation |
| Secrets Scan | No hardcoded credentials |
| Authorization | Appropriate for public endpoint |
| Test Coverage | 94% coverage on new code |

---
_Proof record generated. Merge permitted._`,
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getSeverityColor = (severity: string): 'danger' | 'important' | 'warning' => {
  switch (severity) {
    case 'CRITICAL': return 'danger';
    case 'HIGH': return 'important';
    case 'MEDIUM': return 'warning';
    default: return 'warning';
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CodeGovernanceDemoPage: React.FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();
  const [activeResults, setActiveResults] = useState<Record<string, boolean>>({});

  const handleRunSimulation = (id: string) => {
    setActiveResults((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className={ps.page}>
      <PageHeader
        title="AI Code Governance Demo"
        subtitle="Simulate PR enforcement against seeded sample diffs"
      />

      <div className={styles.scenarioGrid}>
        {scenarios.map((scenario) => {
          const isExpanded = !!activeResults[scenario.id];
          const isPass = scenario.expectedDecision === 'PASS';

          return (
            <Card key={scenario.id}>
              <CardHeader
                header={<Title2>{scenario.name}</Title2>}
                description={
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, marginTop: tokens.spacingVerticalXXS }}>
                    <Caption1>Expected:</Caption1>
                    <Badge
                      appearance="filled"
                      color={isPass ? 'success' : 'danger'}
                      size="small"
                    >
                      {scenario.expectedDecision}
                    </Badge>
                  </div>
                }
              />
              <div className={ps.cardBody}>
                <div className={styles.scenarioCard}>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                    {scenario.description}
                  </Body2>

                  {/* Code diff preview */}
                  <pre style={{
                    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
                    fontSize: tokens.fontSizeBase200,
                    lineHeight: tokens.lineHeightBase300,
                    padding: tokens.spacingVerticalM,
                    backgroundColor: tokens.colorNeutralBackground2,
                    borderRadius: tokens.borderRadiusMedium,
                    overflowX: 'auto',
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {scenario.codeDiff}
                  </pre>

                  <Button
                    appearance={isExpanded ? 'subtle' : 'primary'}
                    icon={<PlayRegular />}
                    onClick={() => handleRunSimulation(scenario.id)}
                  >
                    {isExpanded ? 'Hide Result' : 'Run Simulation'}
                  </Button>

                  {/* Simulation Result */}
                  {isExpanded && (
                    <div className={styles.resultContainer}>
                      {/* Decision */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                        {isPass ? (
                          <CheckmarkCircleRegular style={{ fontSize: '24px', color: tokens.colorPaletteGreenForeground1 }} />
                        ) : (
                          <DismissCircleRegular style={{ fontSize: '24px', color: tokens.colorPaletteRedForeground1 }} />
                        )}
                        <Title2 style={{ color: isPass ? tokens.colorPaletteGreenForeground1 : tokens.colorPaletteRedForeground1 }}>
                          {scenario.expectedDecision}
                        </Title2>
                      </div>

                      {/* Findings */}
                      {scenario.findings.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
                          <Body1 style={{ fontWeight: 600 }}>
                            {scenario.findings.length} Finding{scenario.findings.length !== 1 ? 's' : ''} Detected
                          </Body1>
                          {scenario.findings.map((f, idx) => (
                            <div key={idx} className={styles.findingItem}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                                <Badge appearance="filled" color={getSeverityColor(f.severity)} size="small">
                                  {f.severity}
                                </Badge>
                                <Badge appearance="outline" size="small">{f.policyKey}</Badge>
                                <Body1 style={{ fontWeight: 500 }}>{f.title}</Body1>
                              </div>
                              <Body2 style={{ color: tokens.colorNeutralForeground3, fontStyle: 'italic' }}>
                                {f.impact}
                              </Body2>
                              <Body2>
                                <span style={{ fontWeight: 600 }}>Fix: </span>
                                {f.fix}
                              </Body2>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                          <CheckmarkCircleRegular style={{ color: tokens.colorPaletteGreenForeground1 }} />
                          <Body1>All governance checks passed. No findings detected.</Body1>
                        </div>
                      )}

                      <Divider />

                      {/* PR Comment Preview */}
                      <div>
                        <Body1 style={{ fontWeight: 600, marginBottom: tokens.spacingVerticalS, display: 'block' }}>
                          GitHub PR Comment Preview
                        </Body1>
                        <div className={styles.prComment}>
                          <div className={styles.commentHeader}>
                            <Badge appearance="filled" color="brand" size="small">bot</Badge>
                            <Caption1 style={{ fontWeight: 600 }}>leftshield-bot</Caption1>
                            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>commented just now</Caption1>
                          </div>
                          {scenario.prCommentPreview}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
